import React, { useContext, useState } from "react";

import { Dropdown, Menu } from "antd";
import type { ExpandIconProps } from "antd/lib/table";
import _ from "lodash";
import capitalize from "lodash/capitalize";
import isEmpty from "lodash/isEmpty";

import { useLegalAuditDocumentRedaction } from "manager/hooks/useLegalAuditDocumentRedaction";
import type { IncomeStream } from "renter/interfaces/api/income";
import FlexContainer from "shared/components/FlexContainer";
import Icon from "shared/components/Icon";
import {
  ConvertedAmount,
  getDisplayCurrency,
  INTERNATIONAL_USD_TOOLTIP,
  shouldShowConversionDetails,
} from "shared/components/InternationalConversion";
import { openMessenger } from "shared/components/Messenger/messengerUtils";
import Tag from "shared/components/Tag";
import Tile from "shared/components/Tile";
import { CollapseButton } from "shared/components/Tile/styled";
import Tooltip from "shared/components/Tooltip";
import { RegularText, SmallText } from "shared/components/Typography";
import {
  APPLICATION_STATUSES,
  MAX_INCOMES_NUMBER_IN_STREAM,
} from "shared/config/constants";
import { usePermissions } from "shared/hooks";
import { prefetchOcrResultById } from "shared/hooks/api/applicationQueries";
import useModal from "shared/hooks/useModal";
import type { OcrResult } from "shared/interfaces/api/document";

import { isManagerApp } from "shared/utils/auth";
import { getFraudRiskTextAndColor } from "shared/utils/document";
import { printDollarsFromCents } from "shared/utils/dollar-print";
import {
  getInternationalFields,
  getInternationalVerificationLabel,
  hasInternationalField,
  isInternationalStream,
  mapIncomeStreamVerificationMethodToSource,
} from "shared/utils/employmentAndIncome";
import { useVeroFlag } from "shared/utils/featureFlags";
import {
  getExclusionColumnHeader,
  getExclusionDisplayItems,
  shouldShowVeroIncomeStreamFootnote,
} from "shared/utils/incomeStreamExclusionDisplay";
import { VeroFlag } from "shared/utils/veroFlags";

import ApplicationContext from "../../applicationContext";

import { EditIncomeStreamModal } from "./EditIncomeStreamModal";
import { StreamDetailTable } from "./StreamDetailTable";
import {
  IncomeStreamsTable as StyledTable,
  IncomeStreamHeader,
  MoreLink,
  StyledMenu,
} from "./styled";

interface IncomeStreamsTableProps {
  streams: IncomeStream[];
  type: "excluded" | "included";
  ocrResults?: OcrResult[];
  canViewOcrResults: boolean;
  canEditOcrResults: boolean;
}

const incomeStreamTableId = "income-streams-table";

export const IncomeStreamsTable = ({
  streams,
  type,
  ocrResults,
  canViewOcrResults,
  canEditOcrResults,
}: IncomeStreamsTableProps) => {
  const { application } = useContext(ApplicationContext);
  const { applicantVrn, dealDecisionIsMade, status } = application || {};
  const { canEditIncomeStreams } = usePermissions();
  const { shouldRedactDocuments } = useLegalAuditDocumentRedaction();
  const showInternationalIncome = useVeroFlag(
    VeroFlag.useInternationalDocumentUpload
  );
  const intlOcrFixesEnabled = useVeroFlag(VeroFlag.useIntlOcrFixes);
  const showConversionBreakdown =
    showInternationalIncome && intlOcrFixesEnabled;
  const isManager = isManagerApp();
  const applicantSafeIncomeExclusionCtasEnabled = useVeroFlag(
    VeroFlag.applicantSafeIncomeExclusionCtas
  );
  const useApplicantSafeCtas =
    !isManager && applicantSafeIncomeExclusionCtasEnabled;
  const [viewExclusion, setViewExclusion] = useState({});
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  const renderFraudRisk = isManager && canViewOcrResults && type === "excluded";
  const renderExclusionReasons = type === "excluded";
  const showVeroIncomeStreamFootnote =
    type === "excluded"
      ? shouldShowVeroIncomeStreamFootnote(useApplicantSafeCtas)
      : true;

  // The backend can return two stream rows that share the same id (e.g. when a
  // single paystub file is split across fraudulent/non-fraudulent partitions,
  // both end up hashing to the same identifier). AntD keys both rowKey and
  // expansion state off the same value, so a colliding id makes clicking one
  // row expand both. Composing the id with the array position keeps rows
  // distinguishable without hiding the underlying backend duplication.
  const getRowKey = (stream: IncomeStream, index: number) =>
    `${stream.id}:${index}`;

  const getHighestFraudRiskLevel = (ocrResultIds) => {
    const results = _.map(ocrResultIds, (sourceOcrResult) =>
      _.find(
        ocrResults,
        (ocrResult) =>
          ocrResult?.id === sourceOcrResult ||
          ocrResult?.ocrResultId === sourceOcrResult
      )
    );
    const riskLevels = _.chain(results).map("fraudRiskLevel").value();

    if (_.includes(riskLevels, "VERY_HIGH")) {
      return "VERY_HIGH";
    }
    if (_.includes(riskLevels, "HIGH")) {
      return "HIGH";
    }
    if (_.includes(riskLevels, "MEDIUM")) {
      return "MEDIUM";
    }
    if (_.includes(riskLevels, "LOW")) {
      return "LOW";
    }
    return "UNSUPPORTED";
  };

  const determineFraudRisk = (ocrResultIds) => {
    const riskLevel = getHighestFraudRiskLevel(ocrResultIds);
    return getFraudRiskTextAndColor(riskLevel);
  };

  const handleMoreClick = (identifier) => {
    setViewExclusion({
      ...viewExclusion,
      [identifier]: !viewExclusion[identifier],
    });
  };

  const { openModalDialog } = useModal();

  const columns = [
    {
      title: <IncomeStreamHeader>Source</IncomeStreamHeader>,
      dataIndex: "source",
      render: (_, { description }) => <RegularText>{description}</RegularText>,
    },
    ...(renderExclusionReasons
      ? [
          {
            title: (
              <IncomeStreamHeader>
                {getExclusionColumnHeader(useApplicantSafeCtas)}
              </IncomeStreamHeader>
            ),
            dataIndex: "excludedReasons",
            render: (_text, stream) => {
              const { identifier } = stream;
              const displayItems = getExclusionDisplayItems(stream, !isManager);
              const isExpanded = viewExclusion[identifier];
              const hasMoreThanTwoReasons = displayItems.length > 2;

              return (
                <FlexContainer
                  flexDirection="column"
                  onClick={(e) => {
                    if (hasMoreThanTwoReasons) {
                      e.stopPropagation();
                      handleMoreClick(identifier);
                    }
                  }}
                  style={{
                    cursor: hasMoreThanTwoReasons ? "pointer" : "default",
                  }}
                >
                  <ul
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                      paddingLeft: "20px",
                      margin: 0,
                    }}
                  >
                    {_.map(displayItems, (reason, index) => (
                      <li
                        key={index}
                        style={{
                          listStyleType: "unset",
                          display:
                            Number(index) < 2 || isExpanded
                              ? "list-item"
                              : "none",
                        }}
                      >
                        <RegularText>{reason}</RegularText>
                      </li>
                    ))}
                  </ul>
                  {hasMoreThanTwoReasons && (
                    <MoreLink
                      style={{
                        marginTop: "8px",
                        alignSelf: "flex-start",
                      }}
                    >
                      {isExpanded ? "Show less" : "Show more"}
                    </MoreLink>
                  )}
                </FlexContainer>
              );
            },
          },
        ]
      : []),
    ...(renderFraudRisk
      ? [
          {
            title: (
              <FlexContainer alignItems="center" flexDirection="row" gap="4px">
                <IncomeStreamHeader>Fraud Risk</IncomeStreamHeader>
                <Icon.BlueQuestionMark
                  height={14}
                  cursor="pointer"
                  width={14}
                  onClick={() => openMessenger()}
                />
              </FlexContainer>
            ),
            dataIndex: "fraudRisk",
            render: (_text, { incomes }) => {
              const sourceOcrResults = _.map(
                incomes,
                (income) => income.sourceOcrResult
              );
              const fraudRisk = determineFraudRisk(sourceOcrResults);
              return (
                <RegularText style={{ color: fraudRisk.color }}>
                  {fraudRisk.text}
                </RegularText>
              );
            },
          },
        ]
      : []),
    {
      title: <IncomeStreamHeader>Verification</IncomeStreamHeader>,
      dataIndex: "verification",
      render: (
        _,
        { annotationDetail, streamVerificationMethod, ...stream }
      ) => {
        const internationalFields = getInternationalFields(
          stream as IncomeStream
        );
        const {
          country,
          currency: intlCurrency,
          originalCurrency: intlOrigCurrency,
        } = internationalFields;
        const international =
          showConversionBreakdown &&
          isInternationalStream(streamVerificationMethod, internationalFields);
        const verificationLabel =
          (international &&
            getInternationalVerificationLabel(
              streamVerificationMethod,
              country
            )) ||
          mapIncomeStreamVerificationMethodToSource(streamVerificationMethod);
        const intlDetail = country || intlOrigCurrency || intlCurrency;
        const displayCurrency = getDisplayCurrency(internationalFields);
        const showIntlUsdClarificationTooltip =
          international &&
          hasInternationalField(displayCurrency) &&
          String(displayCurrency).toUpperCase() === "USD";
        const pillHasTooltip = showIntlUsdClarificationTooltip;
        const pillLabel = intlDetail
          ? `International (${intlDetail})`
          : "International";
        const internationalPill = (
          <Tag size="md" backgroundColor="#EEF2FF" color="#4338CA">
            {pillHasTooltip ? (
              <span
                style={{
                  display: "inline-block",
                  borderBottom: "1px dashed #4338CA",
                  cursor: "pointer",
                }}
              >
                {pillLabel}
              </span>
            ) : (
              pillLabel
            )}
          </Tag>
        );

        return (
          <FlexContainer
            flexDirection="column"
            alignItems="flex-start"
            gap="4px"
          >
            <RegularText>{verificationLabel}</RegularText>
            {isManager && annotationDetail?.lastModifiedBy?.name && (
              <SmallText light>
                (Manually {type === "excluded" ? "excluded" : "included"} by{" "}
                {annotationDetail.lastModifiedBy.name})
              </SmallText>
            )}
            {international &&
              (showIntlUsdClarificationTooltip ? (
                <Tooltip title={INTERNATIONAL_USD_TOOLTIP}>
                  <span style={{ display: "inline-block" }}>
                    {internationalPill}
                  </span>
                </Tooltip>
              ) : (
                internationalPill
              ))}
          </FlexContainer>
        );
      },
    },
    {
      title: <IncomeStreamHeader>Pay Frequency</IncomeStreamHeader>,
      dataIndex: "payFrequency",
      render: (_, { frequency }) => (
        <RegularText>{capitalize(frequency)}</RegularText>
      ),
    },
    {
      title: <IncomeStreamHeader>Projected Annual Income</IncomeStreamHeader>,
      dataIndex: "income",
      render: (
        _,
        {
          estimatedAnnualGrossAmount,
          estimatedAnnualNetAmount,
          streamVerificationMethod,
          ...stream
        }
      ) => {
        const internationalFields = getInternationalFields(
          stream as IncomeStream
        );
        const showConversionDetails = shouldShowConversionDetails(
          showConversionBreakdown,
          streamVerificationMethod,
          internationalFields
        );

        const dollarAmount = (
          <RegularText>
            {printDollarsFromCents(
              estimatedAnnualGrossAmount ?? estimatedAnnualNetAmount ?? 0
            )}{" "}
            {type === "included" &&
              `(${estimatedAnnualGrossAmount ? "Gross" : "Net"})`}
          </RegularText>
        );

        if (!showConversionDetails) return dollarAmount;

        return (
          <ConvertedAmount fields={internationalFields}>
            {dollarAmount}
          </ConvertedAmount>
        );
      },
    },
    ...(isManagerApp()
      ? [
          {
            title: "",
            width: 50,
            render: (_, stream) => {
              const menuItems = [
                ...(!dealDecisionIsMade &&
                status !== APPLICATION_STATUSES.inProgress &&
                canEditIncomeStreams &&
                !shouldRedactDocuments
                  ? [
                      <Menu.Item
                        key={stream.identifier}
                        onClick={() => {
                          openModalDialog(EditIncomeStreamModal, {
                            applicantVrn,
                            stream,
                            type,
                          });
                        }}
                      >{`${
                        type === "excluded" ? "Include" : "Exclude"
                      } Income Stream`}</Menu.Item>,
                    ]
                  : []),
              ];

              if (menuItems.length === 0) return null;

              return (
                <Dropdown
                  getPopupContainer={(triggerNode) =>
                    document.getElementById(incomeStreamTableId) ?? triggerNode
                  }
                  placement="bottomRight"
                  overlay={
                    <StyledMenu
                      onClick={({ domEvent }) => domEvent.stopPropagation()}
                    >
                      {menuItems.map((item) => item)}
                    </StyledMenu>
                  }
                  trigger={["click"]}
                >
                  <Icon.DropdownIcon
                    height={50}
                    style={{
                      cursor: "pointer",
                      display: "block",
                      margin: "auto",
                      transform: "rotate(90deg)",
                    }}
                    width={24}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  />
                </Dropdown>
              );
            },
          },
          {
            title: "",
            render: null,
          },
        ]
      : []),
  ];

  return (
    <>
      {!isEmpty(streams) && (
        <Tile.Inner noPaddingTop>
          {showVeroIncomeStreamFootnote && (
            <FlexContainer alignItems="center" flexDirection="row" gap="8px">
              <Icon.InfoBlueIcon height={16} />
              <SmallText>
                {`VERO displays up to ${MAX_INCOMES_NUMBER_IN_STREAM} supporting documents or transactions for
              each income stream.`}
              </SmallText>
            </FlexContainer>
          )}
          <StyledTable
            // @ts-ignore
            id={incomeStreamTableId}
            columns={columns}
            expandIcon={({
              record,
              expanded,
              onExpand,
            }: ExpandIconProps<IncomeStream>) =>
              !isEmpty(record?.incomes) ? (
                <CollapseButton
                  collapsed={!expanded}
                  collapsibleOnDesktop
                  collapsibleOnMobile
                  onClick={onExpand}
                  style={{ float: "right" }}
                >
                  <Icon.ChevronDownIcon
                    style={{ display: "block", margin: "auto" }}
                  />
                </CollapseButton>
              ) : undefined
            }
            expandIconAsCell={false}
            expandIconColumnIndex={columns.length - 1}
            expandedRowKeys={expandedRowKeys}
            onRow={(record: IncomeStream, index?: number) => ({
              onClick: () => {
                if (isEmpty(record?.incomes)) return;
                const key = getRowKey(record, index ?? 0);
                setExpandedRowKeys((prev) =>
                  prev.includes(key)
                    ? prev.filter((k) => k !== key)
                    : [...prev, key]
                );
              },
              onMouseEnter: () => {
                if (!isManagerApp() || !canViewOcrResults) return;
                if (isEmpty(record?.incomes)) return;
                const sourceOcrIds = _.chain(record.incomes)
                  .map((income) => _.get(income, "sourceOcrResult"))
                  .compact()
                  .uniq()
                  .value();
                sourceOcrIds.forEach((id) => prefetchOcrResultById(id));
              },
            })}
            rowClassName={(record: IncomeStream) =>
              isEmpty(record?.incomes) && "not-expandable"
            }
            expandRowByClick
            expandedRowRender={(stream) => (
              <StreamDetailTable
                incomeStream={stream as IncomeStream}
                canViewOcrResults={canViewOcrResults}
                canEditOcrResults={canEditOcrResults}
              />
            )}
            dataSource={streams}
            noBorderBottom
            noClickableRows
            pagination={false}
            rowKey={getRowKey}
            scroll={{ x: true }}
            size="middle"
          />
        </Tile.Inner>
      )}
      {isEmpty(streams) && (
        <Tile.Inner key={0} data-testid={`income-stream-${0}`} noPaddingTop>
          <FlexContainer
            alignItems="center"
            flexDirection="row"
            justifyContent="center"
            gap="8px"
          >
            <Icon.InfoBlueIcon />
            <RegularText>
              {type === "excluded"
                ? "There is no excluded income available for this application."
                : "There is no income available for this application."}
            </RegularText>
          </FlexContainer>
        </Tile.Inner>
      )}
    </>
  );
};
